import { useCustom, useApiUrl } from '@refinedev/core';
import { Database, Globe, Users, Receipt, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { FormHeader } from '@/components/forms';
import { cn } from '@/lib/utils';
import type { TenantListItem } from './types';

function StatusBadge({ status }: { status: TenantListItem['status'] }) {
  if (status === 'online') {
    return (
      <Badge variant="secondary" className="gap-1 text-green-600 dark:text-green-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> Online
      </Badge>
    );
  }
  if (status === 'offline') {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3.5 w-3.5" /> Offline
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1">
      <HelpCircle className="h-3.5 w-3.5" /> Неизвестно
    </Badge>
  );
}

function TenantCard({ tenant }: { tenant: TenantListItem }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 rounded-md">
            {tenant.logoUrl ? <AvatarImage src={tenant.logoUrl} alt={tenant.name} /> : null}
            <AvatarFallback className="rounded-md bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate">{tenant.name}</CardTitle>
            <CardDescription className="truncate font-mono text-xs">{tenant.slug}</CardDescription>
          </div>
          <StatusBadge status={tenant.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {tenant.domains && tenant.domains.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
            <Globe className="h-4 w-4 shrink-0" />
            {tenant.domains.map((domain) => (
              <Badge key={domain} variant="outline" className="font-normal">
                {domain}
              </Badge>
            ))}
          </div>
        ) : null}
        <div className="flex gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {tenant.counts?.users ?? '—'}
          </span>
          <span className="flex items-center gap-1.5">
            <Receipt className="h-4 w-4" />
            {tenant.counts?.orders ?? '—'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Страница «Базы данных» — список тенантов, доступных админу.
 *
 * Только просмотр: создание/удаление БД — задача разработчика через TENANTS_CONFIG,
 * кнопок create/delete здесь нет (см. 04-admin.md §4.3).
 */
export function DatabasesList() {
  const apiUrl = useApiUrl();
  const { query } = useCustom<TenantListItem[]>({
    url: `${apiUrl}/admin/tenants`,
    method: 'get',
  });

  const tenants = query.data?.data ?? [];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Базы данных"
        description="Тенанты, к которым у вас есть доступ. Просмотр и настройка функционала."
      />

      {query.isLoading ? (
        <div className={cn('grid gap-4', 'sm:grid-cols-2 xl:grid-cols-3')}>
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Нет доступных баз данных.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {tenants.map((tenant) => (
            <TenantCard key={tenant.slug} tenant={tenant} />
          ))}
        </div>
      )}
    </div>
  );
}
